import { Component, OnDestroy, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { InvoiceService } from '../../service/data/invoice.service';
import { Invoice } from '../../model/invoice';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { FormBuilder, FormGroup, AbstractControl, ValidationErrors, FormArray } from '@angular/forms';
import { of, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { LeaseAgreementService } from '../../../rent/service/data/lease-agreement.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { LocalDate } from '../../../core/model/local-date';
import { Money } from '../../../core/model/money';
import { CreditInvoiceResultModel, CreditInvoiceRowModel } from '../../model/credit-invoice';

@Component({
    selector: 'app-credit-invoice-form',
    templateUrl: './credit-invoice-form.component.html',
    styleUrls: ['./credit-invoice-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreditInvoiceFormComponent implements OnDestroy, OnInit {
  constructor(
    private _dialogService: ActionWindowService,
    private _dataService: InvoiceService,
    private _leaseAgreementService: LeaseAgreementService,
    private _fb: FormBuilder,
    private _fcf: FormControlExFactory
  ) {}

  @Input() dataContext: Invoice;

  leaseAgreements: SelectionItem[] = [];
  private _s: Subscription = new Subscription();
  form: FormGroup;
  hasOngoingAmortizationPlan: boolean;

  private _validateAmount(control: AbstractControl): ValidationErrors {
    if (!this.dataContext) return null;

    let maxAmount = this.maxCreditAmount ? this.maxCreditAmount.amount : this.dataContext.amount.amount;
    return control.value.amount > maxAmount ? { amount: { message: 'ERROR.CREDIT_INVOICE_AMOUNT' } } : null;
  }

  customerBalance: Money;
  maxCreditAmount: Money;

  cancel() {
    this._dialogService.close();
  }

  ngOnDestroy() {
    this._s.unsubscribe();
    this._initSub.unsubscribe();
  }

  private _initSub: Subscription = new Subscription();
  get creditRows(): boolean {
    return this.form ? this.form.controls['creditRows'].value : false;
  }

  creditMax(rowId: string) {
    let row = (this.form.controls['rows'] as FormArray).controls.map(c => c as FormGroup).find(r => r.controls['id'].value === rowId);
    var maxCreditableAmount = new Money(
      this.dataContext.currencyCode,
      row.controls['invoicedAmount'].value.amount - row.controls['creditedAmount'].value.amount
    );
    row.controls['correction'].setValue(maxCreditableAmount);
  }

  createRowsForm(rows: CreditInvoiceRowModel[]) {
    const rowForms = this.form.controls['rows'] as FormArray;
    for (let row of rows) {
      let rowForm = this._fb.group({
        id: this._fcf.createHidden(row.id),
        description: this._fcf.createReadonly('COMMON.DESCRIPTION', row.description),
        invoicedAmount: this._fcf.createReadonly(
          row.vatAmount.amount === 0 ? 'INVOICE.CREDIT_ROWS.INVOICED_AMOUNT' : 'INVOICE.CREDIT_ROWS.INVOICED_AMOUNT_EX_VAT',
          row.invoicedAmount
        ),
        vatAmount: this._fcf.createReadonly('INVOICE.CREDIT_ROWS.VAT', row.vatAmount),
        paidAmount: this._fcf.createReadonly('INVOICE.CREDIT_ROWS.PAID_AMOUNT', row.paidAmount),
        creditedAmount: this._fcf.createReadonly('INVOICE.CREDIT_ROWS.PREV_CREDIT', row.creditedAmount),
        balance: this._fcf.createReadonly(
          row.vatAmount.amount === 0 ? 'INVOICE.CREDIT_ROWS.BALANCE' : 'INVOICE.CREDIT_ROWS.BALANCE_INC_VAT',
          row.balance
        ),
        correction: this._fcf.createMandatory(
          row.vatAmount.amount === 0 ? 'INVOICE.CREDIT_ROWS.ADJUSTMENT' : 'INVOICE.CREDIT_ROWS.ADJUSTMENT_EX_VAT',
          new Money(this.dataContext.currencyCode, 0)
        ),
        newBalance: this._fcf.createReadonly('INVOICE.CREDIT_ROWS.NEW_BALANCE', row.balance),
        isRounding: this._fcf.createHidden(row.isRounding),
        allowReInvoicing: this._fcf.createHidden(false),
      });
      rowForms.push(rowForm);
    }

    this._s.add(
      rowForms.valueChanges
        .pipe(
          switchMap(val => {
            let prm = this.form.value;
            prm.rows = rowForms.value;
            return this._updating ? of(new CreditInvoiceResultModel()) : this._dataService.previewCredit(prm);
          })
        )
        .subscribe(result => {
          if (!this._updating) {
            this.updateBalancesAndValidity(result);
          }
        })
    );
    this.updateSummary();
  }

  errorMessage: string;

  get rowForms(): FormGroup[] {
    return (this.form.controls['rows'] as FormArray).controls.map(c => c as FormGroup);
  }

  private _updating: boolean;
  private updateBalancesAndValidity(preview: CreditInvoiceResultModel) {
    this.customerBalance = preview.customerBalance.amount !== 0 ? preview.customerBalance : null;
    this.errorMessage = preview.errorMessage;
    let rowForms = (this.form.controls['rows'] as FormArray).controls.map(c => c as FormGroup);
    for (let row of preview.rows) {
      let targetRow = row.isRounding
        ? rowForms.find(r => r.controls['isRounding'].value)
        : rowForms.find(r => r.controls['id'].value === row.id);

      this._updating = true;

      if (!targetRow.controls['isRounding'].value) {
        let correction = targetRow.controls['correction'].value;
        if (targetRow.controls['invoicedAmount'].value.amount > 0 && correction.amount > 0) {
          targetRow.controls['correction'].setValue(new Money(correction.currencyCode, -correction.amount));
        }
        if (targetRow.controls['invoicedAmount'].value.amount < 0 && correction.amount < 0) {
          targetRow.controls['correction'].setValue(new Money(correction.currencyCode, -correction.amount));
        }
      }

      targetRow.controls['newBalance'].setValue(row.balance);
      this._updating = false;
    }
    this.updateSummary();
  }

  private updateSummary() {
    let currency = this.dataContext.currencyCode;
    let invoicedAmount = new Money(currency, 0);
    let creditedAmount = new Money(currency, 0);
    let paidAmount = new Money(currency, 0);
    let balance = new Money(currency, 0);
    let correction = new Money(currency, 0);
    let newBalance = new Money(currency, 0);
    for (let formRow of (this.form.controls['rows'] as FormArray).controls) {
      let row = formRow as FormGroup;
      invoicedAmount.amount += row.controls['invoicedAmount'].value.amount;
      creditedAmount.amount += row.controls['creditedAmount'].value.amount;
      paidAmount.amount += row.controls['paidAmount'].value.amount;
      balance.amount += row.controls['balance'].value.amount;
      correction.amount += row.controls['correction'].value.amount;
      newBalance.amount += row.controls['newBalance'].value.amount;
    }
    this.sumForm.controls['invoicedAmount'].setValue(invoicedAmount);
    this.sumForm.controls['creditedAmount'].setValue(creditedAmount);
    this.sumForm.controls['paidAmount'].setValue(paidAmount);
    this.sumForm.controls['balance'].setValue(balance);
    this.sumForm.controls['correction'].setValue(correction);
    this.sumForm.controls['newBalance'].setValue(newBalance);
  }

  sumForm: FormGroup;

  ngOnInit() {
    this.form = this._fb.group({
      amount: this._fcf.createWithOptions('INVOICE.CREDIT_INVOICE_AMOUNT', new Money(this.dataContext.currencyCode, 0), {
        required: true,
        validator: this._validateAmount.bind(this),
      }),
      id: this._fcf.createHidden(this.dataContext.id),
      verificationDescription: this._fcf.create('ACCOUNT_TRANSACTION.DESCRIPTION', null),
      leaseAgreementId: this._fcf.create('INCORRECT_PAYMENT.LEASE', null),
      creditRows: this._fcf.create('INVOICE.CREDIT_INDIVIDUAL_ROWS', false),
      rows: this._fb.array([]),
    });

    this.sumForm = this._fb.group({
      invoicedAmount: this._fcf.createReadonly('', new Money(this.dataContext.currencyCode, 0)),
      creditedAmount: this._fcf.createReadonly('', new Money(this.dataContext.currencyCode, 0)),
      paidAmount: this._fcf.createReadonly('', new Money(this.dataContext.currencyCode, 0)),
      balance: this._fcf.createReadonly('INVOICE.CREDIT_ROWS.BALANCE', new Money(this.dataContext.currencyCode, 0)),
      correction: this._fcf.createReadonly('INVOICE.CREDIT_ROWS.ADJUSTMENT', new Money(this.dataContext.currencyCode, 0)),
      newBalance: this._fcf.createReadonly('INVOICE.CREDIT_ROWS.NEW_BALANCE', new Money(this.dataContext.currencyCode, 0)),
    });

    this._s.add(
      this.form.controls['amount'].valueChanges.subscribe(value => {
        this.customerBalance = new Money(
          this.dataContext.currencyCode,
          Math.max(value.amount - this.dataContext.remainingAmount.amount, 0)
        );
      })
    );
    this.form.controls['amount'].setValue(this.dataContext.remainingAmount, { emitEvent: false });

    this._initSub.add(
      this._dataService
        .hasOngoingAmortizationPlan(this.dataContext.id)
        .subscribe(result => (this.hasOngoingAmortizationPlan = result))
    );

    this._initSub.add(
      this._dataService.getCreditInvoiceData(this.dataContext.id).subscribe(data => {
        this.form.controls['verificationDescription'].setValue(data.verificationDescription, { emitEvent: false });
        this.form.controls['amount'].setValue(data.maxCreditAmount);
        this.maxCreditAmount = data.maxCreditAmount;
        this.createRowsForm(data.rows);
      })
    );

    this._initSub.add(
      this._leaseAgreementService.getByPayerSelection(this.dataContext.invoiceRecipientContactId).subscribe(result => {
        result.splice(0, 0, new SelectionItem('ALL_TEXT', null));
        this.leaseAgreements = result;
      })
    );
  }

  onSubmit() {
    if (this.form.invalid || this.errorMessage) return;
    this._updating = true;
    this.form.disable();
    if (this.creditRows) {
      this._s.add(
        this._dataService
          .creditInvoice(this.form.value)
          .pipe(
            catchError(e => {
              this.form.enable();
              throw e;
            })
          )
          .pipe(
            tap(res => {
              if (res.errorMessage) {
                this.errorMessage = res.errorMessage;
                this.form.enable();
                this._updating = true;
              } else {
                this._dialogService.complete(res);
              }
            })
          )
          .subscribe()
      );
    } else {
      this._s.add(
        this._dataService
          .credit(this.form.value)
          .pipe(
            catchError(e => {
              this.form.enable();
              this._updating = false;
              throw e;
            })
          )
          .pipe(tap(res => this._dialogService.complete(res)))
          .subscribe()
      );
    }
  }
}
